/* eslint-disable react/jsx-props-no-spreading */
import React, {FC, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import Downshift, {useCombobox} from 'downshift';
import {combineSportsAndCompetitionsItems, sportNames} from 'utils/sportsUtils';

import useSearchHistory from 'hooks/useSearchHistory';

import './styles.scss';
import {BECompetitionDetail} from 'API/types';

import search_magnifier from 'assets/svg/matchcenter_search.svg';
import search_clear from 'assets/svg/matchcenter_search_clear.svg';
import search_delete from 'assets/svg/matchcenter_search_delete.svg';
import back_arrow from 'assets/svg/matchcenter_tabs_arrow.svg';

interface SearchBarProps {
  term?: string;
  competitions?: BECompetitionDetail[];
  onChange: (term: string) => void;
}

const SearchBar: FC<SearchBarProps> = ({term, competitions, onChange}: SearchBarProps) => {
  const searchList = useMemo(() => {
    const allItems = combineSportsAndCompetitionsItems(competitions);
    return allItems.map(i => ({value: i.id, label: i.name}));
  }, [competitions]);

  const [searchHistory, addTermToHistory, deleteIndexFromHistory] = useSearchHistory();
  const [searchResults, setSearchResults] = useState<{value: string; label: string}[]>([]);
  const [value, setValue] = useState(() => {
    if (!term) return '';
    const currentSearchItem = searchList.find(i => i.value === term);
    return currentSearchItem?.label ?? '';
  });

  // BIQ: remove this
  useEffect(() => {
    if (!term) setValue('');
    const currentSearchItem = searchList.find(i => i.value === term);
    if (currentSearchItem) setValue(currentSearchItem.label);
  }, [term, searchList]);

  const {
    isOpen,
    highlightedIndex,
    openMenu,
    closeMenu,
    getMenuProps,
    getInputProps,
    getToggleButtonProps,
    getComboboxProps,
    getItemProps,
  } = useCombobox({
    inputValue: value,
    items: value ? searchResults : searchHistory,
    itemToString: item => (item ? item.label : ''),
    onInputValueChange: ({inputValue: text = ''}) => {
      setValue(text);
      if (!text) {
        closeMenu();
      } else {
        const filteredItems = searchList
          .filter(
            item =>
              item.value.toLowerCase().includes(text.toLowerCase()) ||
              item.label.toLowerCase().includes(text.toLowerCase())
          )
          .slice(0, 9);
        setSearchResults(filteredItems);
      }
    },
    onSelectedItemChange: ({selectedItem}) => {
      if (!selectedItem) return;
      setValue(selectedItem.label);
      addTermToHistory(selectedItem);
      closeMenu();
      onChange(selectedItem.value);
    },
  });

  return (
    <div className="SearchBar">
      <div className="SearchBar__bar" {...getComboboxProps()}>
        {term && (
          <button
            type="button"
            className="SearchBar__backBtn"
            onClick={() => {
              closeMenu();
              setValue('');
              onChange('');
            }}
          >
            <img src={back_arrow} alt="Πίσω" />
          </button>
        )}
        <input
          className="SearchBar__input"
          {...getInputProps({
            placeholder: 'Άθλημα ή Διοργάνωση',
            onFocus: () => {
              if ((value && searchResults.length) || (!value && searchHistory.length)) openMenu();
            },
          })}
        />
        <button
          type="button"
          className={`SearchBar__searchBtn ${
            value || searchHistory.length ? 'SearchBar__searchBtn--active' : ''
          } ${value ? 'SearchBar__searchBtn--clear' : ''}`}
          {...getToggleButtonProps({
            onClick: value ? () => setValue('') : undefined,
          })}
        >
          {value ? (
            <img src={search_clear} alt="Καθαρισμός" />
          ) : (
            <img src={search_magnifier} alt="Αναζήτηση" />
          )}
        </button>
      </div>
      <ul
        {...getMenuProps({'aria-label': 'Αθλήματα και Διοργανώσεις'})}
        className={`SearchBar__list ${
          isOpen && ((value && searchResults.length) || (!value && searchHistory.length))
            ? 'SearchBar__list--open'
            : ''
        } ${!value && searchHistory.length ? 'SearchBar__list--history' : ''}`}
      >
        {isOpen &&
          (value
            ? searchResults.map((item, index) => {
                const regex = new RegExp(value, 'gi');
                const labelHTML = item.label.replace(regex, match => `<b>${match}</b>`);
                return (
                  <li
                    className={`SearchBar__listItem ${
                      index === highlightedIndex ? 'SearchBar__listItem--focused' : ''
                    }`}
                    key={item.value}
                    {...getItemProps({item, index})}
                  >
                    <span dangerouslySetInnerHTML={{__html: labelHTML}} />
                  </li>
                );
              })
            : !!searchHistory.length &&
              searchHistory.map((item, index) => (
                <li
                  className={`SearchBar__listItem ${
                    index === highlightedIndex ? 'SearchBar__listItem--focused' : ''
                  }`}
                  key={item.value}
                  {...getItemProps({item, index})}
                >
                  <span>{item.label}</span>
                  <button
                    type="button"
                    className="SearchBar__clearListItem"
                    onClick={e => {
                      e.stopPropagation();
                      deleteIndexFromHistory(index);
                    }}
                  >
                    <img src={search_delete} alt="Καθαρισμός" />
                  </button>
                </li>
              )))}
      </ul>
    </div>
  );
};

export default React.memo(SearchBar);
